import truncate from "lodash/truncate";
import { useCallback, useReducer } from "react";
import { HandleUploadParams } from "../components/@types/fileanalysisform";
import { NotificationsLevel } from "../lib/enums";
import {
  UPLOAD_ACTIONS,
  uploadReducer,
  uploadState
} from "../modules/header/upload.reducer";
import { show } from "../notifications/notifications";
import api from "../services/api";
import { route, settings } from "../shim/jsbridge";
import useUploadNotifier from "./upload/useUploadNotifier";

const { max_file_upload_size: MAX_UPLOAD_FILE_SIZE_IN_MB } = settings || {};
const maxFileSize = MAX_UPLOAD_FILE_SIZE_IN_MB * (1024 * 1024);
const maxFileSizeDeepScan = 10000 * (1024 * 1024);

type SubmitFileResponse = {
  detail: {
    id: number;
    sha1: string;
    user: number;
    created: string;
    filename: string;
  };
  code: number;
  warnings: Array<{ code: string; message: string }>;
};

const useSampleUpload = () => {
  const [state, dispatch] = useReducer(uploadReducer, uploadState);

  const { notifyUpload } = useUploadNotifier();

  const createControllers = (fls: File[]): Record<string, AbortController> =>
    fls.reduce<Record<string, AbortController>>(
      (acc, file) => ({
        ...acc,
        [file.name]: new AbortController()
      }),
      {}
    );

  const uploadSamples = useCallback(
    ({
      files,
      password,
      uploadToTicloud,
      rlcsParams,
      rlcsInteractive,
      dynamicAnalysis,
      localOnly,
      sources
    }: HandleUploadParams) => {
      if (!files?.length) return;

      if (MAX_UPLOAD_FILE_SIZE_IN_MB < 0) {
        return;
      }
      const showDeepScanPromo = files.some(
        item => item.size >= maxFileSize && item.size < maxFileSizeDeepScan
      );
      const showMaxFileSizeNotification = files.some(
        item => item.size > maxFileSizeDeepScan
      );
      if (showDeepScanPromo) {
        window.tcbase.showMaxFileSizeModal();
      } else if (showMaxFileSizeNotification) {
        show({
          level: NotificationsLevel.WARNING,
          message: `Some file(s) exceed the recommended maximum file size (${MAX_UPLOAD_FILE_SIZE_IN_MB}MB) supported for analysis.
            Please contact support for options.`
        });
      }
      const validFiles = files.filter(file => maxFileSize > file.size);
      show({
        level: NotificationsLevel.INFO,
        message: `Submitting ${validFiles.length} file`.pluralize(
          validFiles.length
        )
      });
      const controllers = createControllers(validFiles);
      const reduced = validFiles.reduce(
        (acc, file) => ({
          ...acc,
          [file.name]: {
            name: file.name,
            size: file.size,
            progress: 0
          }
        }),
        {}
      );
      dispatch({
        type: UPLOAD_ACTIONS.SETUP,
        count: validFiles.length,
        controllers,
        status: reduced
      });
      validFiles.forEach((file, index) => {
        api
          .submitFile(
            file,
            password,
            uploadToTicloud,
            rlcsParams,
            rlcsInteractive,
            dynamicAnalysis,
            localOnly,
            sources,
            (progressEvent: ProgressEvent) => {
              dispatch({
                type: UPLOAD_ACTIONS.UPDATE_PROGRESS,
                status: {
                  [file.name]: {
                    name: file.name,
                    size: file.size,
                    progress: Math.round(
                      (100 * progressEvent.loaded) / progressEvent.total
                    )
                  }
                }
              });
            },
            controllers[file.name].signal
          )
          .then(({ data }: { data: SubmitFileResponse }) => {
            const { detail, warnings } = data || {};
            notifyUpload();

            const sha1 = detail.sha1;
            let hasCloudSandboxWarning = false;

            warnings.forEach(warning => {
              if (warning.code === "cloud_sandbox_already_running") {
                hasCloudSandboxWarning = true;
              }
              show({
                level: NotificationsLevel.WARNING,
                message: warning.message,
                sticky: true
              });
            });

            const isInteractive = rlcsInteractive?.platform;
            if (isInteractive && !localOnly && !hasCloudSandboxWarning) {
              window.open(
                route("rl_interactive_analysis_for_hash", [sha1]),
                "_blank"
              );
            }
          })
          .catch(err => {
            dispatch({
              type: UPLOAD_ACTIONS.ERROR,
              error: { index, fields: err?.data || {} }
            });
            if (err.problem === "CANCEL_ERROR") {
              dispatch({ type: UPLOAD_ACTIONS.CANCEL_UPLOAD, status: file });
              return;
            }
            const errorMessage = err?.response?.data?.detail;
            show({
              level: NotificationsLevel.ERROR,
              message: `${truncate(file.name, {
                length: 32
              })} upload failed${errorMessage ? `: ${errorMessage}` : ""}`
            });
          })
          .finally(() => dispatch({ type: UPLOAD_ACTIONS.DECREASE }));
      });
    },
    [notifyUpload]
  );

  const validateUploadSample = (
    data: HandleUploadParams,
    onSuccess?: () => void
  ) => {
    api
      .validateUploadSample(
        data.uploadToTicloud,
        data.rlcsParams?.platform,
        data.rlcsParams?.geolocation,
        data.rlcsParams?.locale,
        data.rlcsParams?.internet_simulation,
        data.rlcsParams?.timeout,
        data.rlcsParams?.sample_name,
        data.rlcsInteractive?.platform,
        data.rlcsInteractive?.sample_name,
        data.rlcsInteractive?.timeout,
        data.rlcsInteractive?.geolocation,
        data.rlcsInteractive?.locale,
        data.dynamicAnalysis?.join(","),
        data.localOnly
      )
      .then(() => {
        onSuccess?.();
        uploadSamples(data);
      })
      .catch(err => {
        dispatch({
          type: UPLOAD_ACTIONS.ERROR,
          error: { index: 0, fields: err?.data || {} }
        });
        if (err.problem === "CANCEL_ERROR") {
          dispatch({
            type: UPLOAD_ACTIONS.CANCEL_UPLOAD,
            status: data.files[0]
          });
          return;
        }
      });
  };

  const resetState = (): void => {
    dispatch({ type: UPLOAD_ACTIONS.RESET });
  };

  return {
    state,
    uploadSamples: validateUploadSample,
    resetState
  };
};

export default useSampleUpload;
